import { useEffect, useState } from "react";
import { Building2, Loader2, Search, Users } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useToast } from "@/hooks/use-toast";
import { formatApiError } from "@/lib/api-error";
import { useOmieCandidatos, useOmieVincular } from "@/hooks/use-finance-omie";
import type { OmieCliente, OmieTipoCadastro } from "@/types/finance";

interface OmieVinculoPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cliente: OmieCliente;
}

function tipoInicial(cliente: OmieCliente): OmieTipoCadastro {
  if (cliente.vinculo?.freelancer_id || cliente.sugestao?.freelancer_id) return "freelancer";
  return "agencia";
}

/**
 * Diálogo de vínculo manual: qualquer cliente de qualquer empresa do Omie pode
 * ser agência ou freelancer. Escolhe o lado, busca pelo nome/email/CNPJ e liga
 * ao cadastro Omie escolhido. Sempre irmão do Sheet/Dialog que o abriu
 * (nunca aninhado), senão o Radix não renderiza o segundo modal.
 */
export function OmieVinculoPickerDialog({
  open,
  onOpenChange,
  cliente,
}: OmieVinculoPickerDialogProps) {
  const { toast } = useToast();
  const [tipo, setTipo] = useState<OmieTipoCadastro>(() => tipoInicial(cliente));
  const [termoBusca, setTermoBusca] = useState("");
  const [termoDebounced, setTermoDebounced] = useState("");

  useEffect(() => {
    if (!open) return;
    setTipo(tipoInicial(cliente));
    setTermoBusca("");
    setTermoDebounced("");
  }, [open, cliente]);

  useEffect(() => {
    const timer = setTimeout(() => setTermoDebounced(termoBusca), 300);
    return () => clearTimeout(timer);
  }, [termoBusca]);

  const candidatosQuery = useOmieCandidatos({ tipo, q: termoDebounced }, open);
  const vincular = useOmieVincular();

  const candidatos = candidatosQuery.data?.items ?? [];

  const handleEscolher = async (candidatoId: string) => {
    try {
      await vincular.mutateAsync({
        clienteId: cliente.id,
        body: {
          origem: "manual",
          ...(tipo === "agencia" ? { agencia_id: candidatoId } : { freelancer_id: candidatoId }),
        },
      });
      toast({ title: "Vínculo registrado" });
      onOpenChange(false);
    } catch (err) {
      const formatted = formatApiError(err);
      toast({ variant: "destructive", title: formatted.title, description: formatted.detail });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Vincular cadastro Omie</DialogTitle>
          <DialogDescription>
            {cliente.razao_social}
            {cliente.nome_fantasia ? ` (${cliente.nome_fantasia})` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <ToggleGroup
            type="single"
            value={tipo}
            onValueChange={(valor) => {
              if (!valor) return;
              setTipo(valor as OmieTipoCadastro);
            }}
            className="justify-start rounded-md border p-1"
          >
            <ToggleGroupItem value="agencia" className="gap-1.5 px-3">
              <Building2 className="h-4 w-4" />
              Agência
            </ToggleGroupItem>
            <ToggleGroupItem value="freelancer" className="gap-1.5 px-3">
              <Users className="h-4 w-4" />
              Freelancer
            </ToggleGroupItem>
          </ToggleGroup>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={termoBusca}
              onChange={(e) => setTermoBusca(e.target.value)}
              placeholder={tipo === "agencia" ? "Buscar agência..." : "Buscar freelancer..."}
              className="pl-9"
            />
          </div>

          <div className="max-h-72 space-y-1 overflow-y-auto">
            {candidatosQuery.isLoading && (
              <div className="flex items-center justify-center py-6 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            )}
            {!candidatosQuery.isLoading && candidatos.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {termoDebounced ? "Nenhum resultado." : "Digite para buscar."}
              </p>
            )}
            {candidatos.map((candidato) => (
              <button
                key={candidato.id}
                type="button"
                disabled={vincular.isPending}
                onClick={() => handleEscolher(candidato.id)}
                className="flex w-full flex-col items-start rounded-md border p-2.5 text-left text-sm hover:bg-muted disabled:opacity-50"
              >
                <span className="font-medium">{candidato.nome}</span>
                <span className="text-xs text-muted-foreground">
                  {[candidato.email, candidato.cnpj].filter(Boolean).join(" · ") || "Sem email/CNPJ cadastrado"}
                </span>
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
